export const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(v || 0),
  );

export const formatDoc = (d) => {
  const s = (d || "").replace(/\D/g, "");
  if (s.length === 11)
    return s.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (s.length === 14)
    return s.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return d || "";
};

// v10.21 — Mês/ano atuais no fuso Brasil (America/Sao_Paulo).
// Usar isso em todos os filtros de período evita que uma nota emitida
// às 22h BRT do dia 30/09 apareça em "Outubro" porque o browser do servidor
// está em UTC (01/10 01:00).
const _partsBR = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Sao_Paulo",
  year: "numeric", month: "2-digit", day: "2-digit",
}).formatToParts(new Date());
export const mesAtualBR = () => {
  const m = _partsBR().find((p) => p.type === "month")?.value || "1";
  return String(parseInt(m, 10));
};
export const anoAtualBR = () => {
  return _partsBR().find((p) => p.type === "year")?.value || String(new Date().getFullYear());
};
// Dado um ISO qualquer (date ou datetime, com ou sem offset), retorna
// { ano, mes, dia } como strings refletindo o calendário brasileiro.
export const toCalendarBR = (iso) => {
  if (!iso) return { ano: "", mes: "", dia: "" };
  // Se for puro YYYY-MM-DD (sem T), trata como data civil local — sem conversão.
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return { ano: y, mes: String(parseInt(m, 10)), dia: d };
  }
  const d = new Date(s);
  if (isNaN(d)) {
    const [y, m, dd] = s.slice(0, 10).split("-");
    return { ano: y || "", mes: String(parseInt(m || "0", 10)), dia: dd || "" };
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")?.value || "";
  const m = parts.find((p) => p.type === "month")?.value || "";
  const dia = parts.find((p) => p.type === "day")?.value || "";
  return { ano: y, mes: String(parseInt(m || "0", 10)), dia };
};

export const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("pt-BR");
};

export const formatDateBR = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y) return iso;
  return `${d}/${m}/${y}`;
};

// Data local do Brasil (America/Sao_Paulo) em ISO YYYY-MM-DD.
// Usar Intl.DateTimeFormat com timeZone garante que "hoje" seja o dia local do usuário
// brasileiro, e não UTC — evita boletos com vencimento hoje aparecerem como vencidos
// depois das 21h BRT (quando UTC já está no dia seguinte).
export const todayISO = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
};
export const plusDaysISO = (n) => {
  const [y, m, d] = todayISO().split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
};

export const onlyDigits = (s) => (s || "").replace(/\D/g, "");

export const waPhone = (raw) => {
  const d = onlyDigits(raw);
  if (!d) return "";
  return d.startsWith("55") ? d : `55${d}`;
};

export const waMessage = (boleto, empresaNome, tipo = "generico") => {
  const valor = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" })
    .format(Number(boleto.valor || 0));
  const venc = formatDateBR(boleto.vencimento);
  const linha = boleto.linha_digitavel || "";
  const pix = boleto.pix_copia_cola || "";
  const saudacoes = {
    generico: `Olá ${boleto.cliente_nome}! Segue seu boleto ${empresaNome}.`,
    aviso3: `Olá ${boleto.cliente_nome}! Lembrete: seu boleto vence em 3 dias.`,
    aviso1: `Olá ${boleto.cliente_nome}! Amanhã é o vencimento do seu boleto.`,
    atraso: `Olá ${boleto.cliente_nome}. Seu boleto venceu em ${venc}. Se já pagou, desconsidere.`,
  };
  const cabec = saudacoes[tipo] || saudacoes.generico;
  const partes = [
    `${cabec}\n`,
    `💰 *Valor:* ${valor}`,
    `📅 *Vencimento:* ${venc}`,
    linha ? `\n*Linha digitável:*\n\`${linha}\`` : "",
    pix ? `\n*PIX Copia e Cola:*\n\`${pix}\`` : "",
    `\nEm caso de dúvidas, é só responder aqui. Obrigado! — ${empresaNome}`,
  ].filter(Boolean);
  return partes.join("\n");
};

export const waLink = (phoneRaw, message) => {
  const p = waPhone(phoneRaw);
  const enc = encodeURIComponent(message);
  return p ? `https://wa.me/${p}?text=${enc}` : `https://wa.me/?text=${enc}`;
};

export const formatApiError = (detail) => {
  if (detail == null) return "Erro desconhecido. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

/**
 * Extrai mensagem legível de QUALQUER erro (network, CORS, HTTP, timeout).
 * Diferente de `formatApiError`, que só olha `response.data.detail`, este
 * inspeciona o objeto axios inteiro. Usado principalmente no fluxo de
 * login/deploy privado onde o proxy pode barrar antes do backend
 * responder — nesses casos `err.response` é undefined e a UI antiga
 * só mostrava "Erro desconhecido".
 */
export const formatAxiosError = (err) => {
  if (!err) return "Erro desconhecido. Tente novamente.";
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  if (detail) return formatApiError(detail);
  if (status === 401) return "Email ou senha inválidos";
  if (status === 403) return "Acesso negado";
  if (status === 429) return "Muitas tentativas. Aguarde alguns segundos e tente de novo.";
  if (status && status >= 500) return `Servidor indisponível (HTTP ${status})`;
  if (status) return `HTTP ${status}${err.response?.statusText ? " " + err.response.statusText : ""}`;
  // Sem resposta HTTP — provável CORS/network/proxy privado bloqueando
  if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
    return "Sem conexão com o servidor. Cheque sua internet ou o token de acesso privado.";
  }
  if (err.code === "ECONNABORTED") return "Requisição demorou demais (timeout).";
  return err.message || "Erro desconhecido. Tente novamente.";
};

